import React, { useEffect, useMemo, useState } from "react";
import type { Idea, TemplateMeta, FieldDefinition } from "../api";
import { PlayerPreview } from "../components/PlayerPreview";

type Props = {
  templates: TemplateMeta[];
  formats: string[];
  locales: string[];
  initialIdea: Idea | null;
  onSubmit: (params: {
    template: string;
    category: string;
    hookId?: string;
    ideaId?: string;
    props: Record<string, Record<string, unknown>>;
    languages: string[];
    formats: string[];
    renderNow: boolean;
  }) => Promise<void>;
};

function applyValues(template: string, values: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(values).map(([k, v]) => [k, v.replace(/\{(\w+)\}/g, (_m, key: string) => values[key] ?? "")]),
  );
}

function deriveInitialProps(tpl: TemplateMeta, idea: Idea | null): Record<string, unknown> {
  const start: Record<string, unknown> = { ...tpl.defaultProps };
  if (!idea) return start;
  const values = applyValues(tpl.id, { ...idea.values, brand: "Allyver" });

  // Field-specific mapping based on template
  switch (tpl.id) {
    case "HookReveal":
      start.hook = idea.title;
      start.payoff = idea.payoff;
      start.cta = idea.cta;
      break;
    case "PatternInterrupt":
      start.followUp = idea.payoff;
      start.payoff = idea.cta;
      break;
    case "BeforeAfter":
      start.before = values.before ?? idea.payoff;
      start.after = values.after ?? idea.payoff;
      start.cta = idea.cta;
      break;
    case "ListBanger": {
      const items: string[] = [];
      for (let i = 1; i <= 5; i++) {
        const v = values[`item${i}`];
        if (v) items.push(v);
      }
      start.hook = idea.title;
      if (items.length >= 2) start.items = items.slice(0, 5);
      start.cta = idea.cta;
      break;
    }
    case "BigStatement":
      start.statement = values.label ?? idea.title;
      start.proof = values.proof ?? idea.payoff;
      if (values.number) start.bigNumber = values.number;
      start.cta = idea.cta;
      break;
    case "FeatureShowcase": {
      const benefits: string[] = [];
      for (let i = 1; i <= 4; i++) {
        const v = values[`benefit${i}`];
        if (v) benefits.push(v);
      }
      start.title = idea.title;
      start.feature = values.featureName ?? "Allyver";
      if (benefits.length >= 1) start.benefits = benefits.slice(0, 4);
      start.cta = idea.cta;
      break;
    }
    case "TestimonialCard":
      start.quote = values.quote ?? idea.payoff;
      start.persona = values.personaName ?? "Lokaler Händler";
      start.role = values.personaRole ?? "Allyver-Partner";
      if (values.rating) start.rating = parseInt(values.rating, 10);
      start.result = values.result ?? "";
      start.cta = idea.cta;
      break;
    case "ProblemSolution":
      start.problem = values.problem ?? idea.title;
      start.solution = values.solution ?? idea.payoff;
      start.cta = idea.cta;
      break;
    case "CountdownCTA":
      start.hook = idea.title;
      start.offer = values.offer ?? idea.payoff;
      start.cta = idea.cta;
      break;
    default:
      break;
  }

  return start;
}

const FieldEditor: React.FC<{
  field: FieldDefinition;
  value: unknown;
  onChange: (v: unknown) => void;
}> = ({ field, value, onChange }) => {
  if (field.kind === "longtext") {
    return (
      <div className="field">
        <label>{field.label}</label>
        <textarea value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value)} />
        {field.hint ? <span className="helper">{field.hint}</span> : null}
      </div>
    );
  }
  if (field.kind === "list") {
    const list = (Array.isArray(value) ? (value as string[]) : []).slice();
    const max = field.max ?? 6;
    return (
      <div className="field">
        <label>{field.label}</label>
        <div className="list-input">
          {list.map((v, i) => (
            <div key={i} className="list-row">
              <input
                value={v}
                onChange={(e) => {
                  const copy = list.slice();
                  copy[i] = e.target.value;
                  onChange(copy);
                }}
              />
              <button
                className="icon-btn"
                onClick={() => {
                  const copy = list.slice();
                  copy.splice(i, 1);
                  onChange(copy);
                }}
              >
                ×
              </button>
            </div>
          ))}
          {list.length < max ? (
            <button onClick={() => onChange([...list, ""])}>+ Zeile hinzufügen</button>
          ) : null}
        </div>
        {field.hint ? <span className="helper">{field.hint}</span> : null}
      </div>
    );
  }
  if (field.kind === "number") {
    return (
      <div className="field">
        <label>{field.label}</label>
        <input
          type="number"
          value={(value as number) ?? 0}
          min={field.min}
          max={field.max}
          onChange={(e) => onChange(Number(e.target.value))}
        />
        {field.hint ? <span className="helper">{field.hint}</span> : null}
      </div>
    );
  }
  if (field.kind === "ctaKey" && field.options) {
    return (
      <div className="field">
        <label>{field.label}</label>
        <select value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value)}>
          {field.options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        {field.hint ? <span className="helper">{field.hint}</span> : null}
      </div>
    );
  }
  return (
    <div className="field">
      <label>{field.label}</label>
      <input value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value)} />
      {field.hint ? <span className="helper">{field.hint}</span> : null}
    </div>
  );
};

export const Editor: React.FC<Props> = ({ templates, formats, locales, initialIdea, onSubmit }) => {
  const [templateId, setTemplateId] = useState<string>(initialIdea?.template ?? templates[0]?.id ?? "HookReveal");
  const tpl = useMemo(() => templates.find((t) => t.id === templateId) ?? templates[0], [templates, templateId]);

  const [props, setProps] = useState<Record<string, unknown>>(() =>
    tpl ? deriveInitialProps(tpl, initialIdea) : {},
  );
  const [previewFormat, setPreviewFormat] = useState<string>("9:16");
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(["de"]);
  const [selectedFormats, setSelectedFormats] = useState<string[]>(["9:16"]);
  const [submitting, setSubmitting] = useState(false);

  // When initialIdea or template changes, refresh props
  useEffect(() => {
    if (!tpl) return;
    setProps(deriveInitialProps(tpl, initialIdea));
  }, [initialIdea?.id, tpl?.id]);

  if (!tpl) return <div className="empty">Lade Templates…</div>;

  const previewProps = { ...props, locale: selectedLanguages[0] ?? "de" };

  const update = (k: string, v: unknown) => setProps((cur) => ({ ...cur, [k]: v }));

  const handleSubmit = async (renderNow: boolean) => {
    setSubmitting(true);
    try {
      const propsForLanguages: Record<string, Record<string, unknown>> = {};
      for (const lang of selectedLanguages) {
        propsForLanguages[lang] = { ...props, locale: lang };
      }
      await onSubmit({
        template: tpl.id,
        category: initialIdea?.category ?? "custom",
        hookId: initialIdea?.hookId,
        ideaId: initialIdea?.id,
        props: propsForLanguages,
        languages: selectedLanguages,
        formats: selectedFormats,
        renderNow,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="editor">
      <div className="editor-form">
        <h2>{initialIdea?.title ?? "Neues Video"}</h2>
        <div className="sub">
          {initialIdea ? <>Vorgefertigte Idee · {initialIdea.payoff}</> : <>Eigenes Video von Grund auf bauen.</>}
        </div>

        <div className="field">
          <label>Template (Lego-Baustein)</label>
          <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label} · {t.category}
              </option>
            ))}
          </select>
          <span className="helper">{tpl.description}</span>
        </div>

        {tpl.fields.map((f) => (
          <FieldEditor key={f.key} field={f} value={props[f.key]} onChange={(v) => update(f.key, v)} />
        ))}

        <div className="field">
          <label>Sprachen ({selectedLanguages.length} ausgewählt)</label>
          <div className="toggle-row">
            {locales.map((l) => (
              <button
                key={l}
                className={`toggle ${selectedLanguages.includes(l) ? "on" : ""}`}
                onClick={() => {
                  setSelectedLanguages((cur) =>
                    cur.includes(l) ? cur.filter((x) => x !== l) : [...cur, l],
                  );
                }}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <label>Formate ({selectedFormats.length} ausgewählt)</label>
          <div className="toggle-row">
            {formats.map((f) => (
              <button
                key={f}
                className={`toggle ${selectedFormats.includes(f) ? "on" : ""}`}
                onClick={() => {
                  setSelectedFormats((cur) =>
                    cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f],
                  );
                }}
              >
                {f}
              </button>
            ))}
          </div>
          <span className="helper">9:16 = Reels/TikTok · 1:1 = Feed · 16:9 = YouTube/Web</span>
        </div>

        <div className="action-row">
          <button
            className="btn"
            disabled={submitting || selectedLanguages.length === 0 || selectedFormats.length === 0}
            onClick={() => handleSubmit(true)}
          >
            {submitting ? <span className="spinner" /> : "Jetzt rendern"}
          </button>
          <button
            className="btn secondary"
            disabled={submitting}
            onClick={() => handleSubmit(false)}
          >
            In Queue legen
          </button>
        </div>
      </div>

      <aside className="player-card">
        <h3>Live-Vorschau · {previewFormat}</h3>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
          <PlayerPreview
            templateId={tpl.id}
            format={previewFormat}
            inputProps={previewProps}
            durationSeconds={tpl.defaultDurationSeconds}
          />
        </div>
        <div className="toggle-row" style={{ justifyContent: "center" }}>
          {formats.map((f) => (
            <button
              key={f}
              className={`toggle ${previewFormat === f ? "on" : ""}`}
              onClick={() => setPreviewFormat(f)}
            >
              {f}
            </button>
          ))}
        </div>
      </aside>
    </section>
  );
};
